"use strict";
/**
 * ARITMETICA DI CALENDARIO — FORMULE CANONICHE (@leopart/calc).
 *
 * Estrazione 1:1 delle tre primitive che erano PRIVATE dentro `acwr.ts`
 * (`toEpochDays`/`fromEpochDays`/`addDays`, non raggiungibili da nessun altro
 * modulo) più le funzioni di settimana/mese che al modulo Allenamenti
 * servivano e non esistevano da nessuna parte in forma pura.
 *
 * Tutto lavora su stringhe `YYYY-MM-DD` passando da epoch-UTC: esatto
 * attraverso i cambi DST, zero dipendenze (niente dayjs/date-fns — vedi
 * README, target ES2019). La risoluzione del "giorno logico" (che timezone
 * usare per trasformare un istante in una di queste stringhe) resta SEMPRE
 * responsabilità del chiamante (backend/frontend), esattamente come per
 * `acwr.ts` e `srpe.ts`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.aGiorniEpoch = aGiorniEpoch;
exports.daGiorniEpoch = daGiorniEpoch;
exports.sommaGiorni = sommaGiorni;
exports.lunediDi = lunediDi;
exports.giorniDellaSettimana = giorniDellaSettimana;
exports.primoDelMese = primoDelMese;
exports.ultimoDelMese = ultimoDelMese;
exports.meseDi = meseDi;
const MS_PER_DAY = 86400000;
/** YYYY-MM-DD → giorni epoch (UTC): aritmetica di calendario esatta, no DST. */
function aGiorniEpoch(date) {
    const y = Number(date.slice(0, 4));
    const m = Number(date.slice(5, 7));
    const d = Number(date.slice(8, 10));
    return Date.UTC(y, m - 1, d) / MS_PER_DAY;
}
/** giorni epoch → YYYY-MM-DD */
function daGiorniEpoch(days) {
    const dt = new Date(days * MS_PER_DAY);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/**
 * Somma `days` giorni (anche negativi) a una data YYYY-MM-DD.
 * Non si chiama `addDays`: il barrel di `@leopart/calc` è piatto (un solo
 * entry point `.` in `package.json#exports`) e quel nome è già in
 * circolazione lato frontend su `Date` — nomi globalmente non ambigui.
 */
function sommaGiorni(date, days) {
    return daGiorniEpoch(aGiorniEpoch(date) + days);
}
/**
 * Lunedì della settimana (lun–dom) che contiene `iso`. Una domenica ricade
 * nella settimana che è appena finita: l'offset dal lunedì è `(dow+6)%7` con
 * `dow` 0=domenica…6=sabato (stessa convenzione di `Date#getUTCDay`).
 */
function lunediDi(iso) {
    const giorni = aGiorniEpoch(iso);
    const dow = new Date(giorni * MS_PER_DAY).getUTCDay();
    const offset = (dow + 6) % 7;
    return daGiorniEpoch(giorni - offset);
}
/** Le 7 date YYYY-MM-DD della settimana (lun→dom) che inizia al lunedì dato. */
function giorniDellaSettimana(lunedi) {
    return Array.from({ length: 7 }, (_, i) => sommaGiorni(lunedi, i));
}
/** Primo giorno del mese che contiene `iso` (YYYY-MM-01). */
function primoDelMese(iso) {
    return `${iso.slice(0, 7)}-01`;
}
/** Ultimo giorno del mese che contiene `iso` (lunghezza mese corretta, anche bisestili). */
function ultimoDelMese(iso) {
    const anno = Number(iso.slice(0, 4));
    const mese = Number(iso.slice(5, 7));
    // Date.UTC(anno, mese, 0) = giorno 0 del mese successivo = ultimo del mese corrente.
    const dt = new Date(Date.UTC(anno, mese, 0));
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const d = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** Mese di `iso` in forma `YYYY-MM`. */
function meseDi(iso) {
    return iso.slice(0, 7);
}
